import { useEffect, useState } from 'react';
import { Package, Warehouse } from 'lucide-react';
import { Badge, Button, Dialog, Input, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import type { WarehouseStock } from '../../stock/model/stock-warehouses.js';
import type { StockGroupDto } from '@eombor/contracts';
import {
  availableText,
  basketLineOf,
  basketQtyError,
  exceedsAvailable,
  isBasketLineReady,
  type BasketLine,
} from '../model/basket.js';

/**
 * SAVATGA QO'SHISH oynasi.
 *
 * Ilgari qator bosilishi bilan satr savatga "1" miqdor bilan tushardi va
 * haqiqiy miqdor keyin, savatning ichida yozilardi. Ya'ni odam bitta ishni
 * ikki joyda bajarardi: qidiruvda tanlaydi, savatda to'ldiradi — va yarim
 * to'ldirilgan satrlar to'planib qolardi.
 *
 * Endi savol AYNAN tanlagan payt so'raladi: qancha va nima uchun. Savatga
 * TAYYOR satr tushadi.
 *
 * Oyna ombor nomini ham qaytarib aytadi. Kartochkada bir necha ombor
 * yonma-yon turadi va bosilgandan keyin "qaysi biriga bosdim?" degan
 * shubha qolmasligi kerak — talabnoma aynan shu ombor bo'yicha ochiladi.
 */

export interface AddLineDialogProps {
  /** `null` — oyna yopiq. Ochilishi bilan miqdor maydoni tozalanadi. */
  target: { group: StockGroupDto; warehouse: WarehouseStock } | null;
  onClose: () => void;
  onAdd: (line: BasketLine) => void;
}

export const AddLineDialog = ({ target, onClose, onAdd }: AddLineDialogProps) => {
  const [qty, setQty] = useState('1');
  const [note, setNote] = useState('');

  /* Har ochilishda toza boshlanadi: oldingi mahsulotning miqdori yangisiga
     o'tib qolsa, odam uni payqamay tasdiqlab yuborishi mumkin. */
  useEffect(() => {
    if (target === null) return;
    setQty('1');
    setNote('');
  }, [target]);

  if (target === null) return null;

  const { group, warehouse } = target;
  const line: BasketLine = {
    ...basketLineOf({
      nomenclatureId: group.nomenclatureId,
      code: group.code,
      name: group.name,
      unit: group.unit,
      warehouseId: warehouse.warehouseId,
      warehouseName: warehouse.warehouseName,
      availableQty: warehouse.availableQty,
    }),
    qty,
    note,
  };

  const error = basketQtyError(line);
  const over = exceedsAvailable(line);
  const ready = isBasketLineReady(line);

  return (
    <Dialog
      open
      size="sm"
      onClose={onClose}
      title={t('requisition.addLineTitle')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('requisition.cancel')}
          </Button>
          <Button disabled={!ready} onClick={() => onAdd(line)}>
            {t('requisition.addToBasket')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className={cn(
              'flex size-10 shrink-0 items-center justify-center',
              'rounded-[var(--radius-md)] bg-accent-soft text-accent',
            )}
          >
            <Package size={19} />
          </span>
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-sm leading-snug font-semibold text-balance text-text">
              {group.name}
            </span>
            <span className="tabular text-xs text-text-faint">{group.code}</span>
          </span>
        </div>

        {/* QAYSI OMBORDAN — tanlov allaqachon qilingan, oyna uni tasdiqlaydi. */}
        <div
          className={cn(
            'flex items-center gap-2.5 rounded-[var(--radius-md)] border',
            'border-border bg-surface-2 px-3 py-2.5',
          )}
        >
          <Warehouse size={15} aria-hidden className="shrink-0 text-text-faint" />
          <span className="flex min-w-0 flex-col">
            <span className="text-[0.6875rem] tracking-wide text-text-faint uppercase">
              {t('requisition.addLineFrom')}
            </span>
            <span className="min-w-0 truncate text-sm text-text">{warehouse.warehouseName}</span>
          </span>
          <span className="tabular ml-auto shrink-0 text-sm font-medium text-text">
            {String(Math.round(warehouse.availableQty * 1000) / 1000)}{' '}
            <span className="text-xs font-normal text-text-faint">{unitText(group.unit)}</span>
          </span>
        </div>

        <Input
          mono
          autoFocus
          label={t('requisition.lineQty')}
          inputMode="decimal"
          autoComplete="off"
          value={qty}
          onChange={(event) => setQty(event.target.value)}
          suffix={unitText(group.unit)}
          error={error ?? undefined}
          hint={error === null ? availableText(line) : undefined}
        />

        {/* Ogohlantiradi, LEKIN bloklamaydi — yakuniy miqdorni sklad hal
            qiladi va qoldiq ekranga chizilgandan keyin ham o'zgaradi. */}
        {over ? (
          <p className="flex flex-wrap items-center gap-2">
            <Badge tone="warn">{t('requisition.exceedsAvailable')}</Badge>
            <span className="text-xs text-text-faint">
              {t('requisition.exceedsAvailableHint')}
            </span>
          </p>
        ) : null}

        <Input
          label={t('requisition.lineNoteOptional')}
          autoComplete="off"
          maxLength={500}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>
    </Dialog>
  );
};
